import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Download, Search, Users, Mail, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const PAGE_SIZE = 50;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const roleTone = (role) => {
  switch (role) {
    case "donor":   return "bg-cobalt/[0.08] text-cobalt";
    case "student": return "bg-grass/[0.08] text-grass";
    default:        return "bg-olive/[0.08] text-olive";
  }
};

/**
 * WaitlistTab — Admin view of beta_signups.
 *
 * Live table with search + role filter, at-a-glance role/source rollups,
 * and a CSV export that mirrors the current filter. Admin-only.
 */
export const WaitlistTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");   // "" | "donor" | "student" | "either"
  const [sourceFilter, setSourceFilter] = useState(""); // "" | click a chip

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("page_size", String(PAGE_SIZE));
    if (q.trim()) p.set("q", q.trim());
    if (roleFilter) p.set("role", roleFilter);
    if (sourceFilter) p.set("source", sourceFilter);
    return p;
  }, [page, q, roleFilter, sourceFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await client.get(`/admin/beta-signups?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load the waitlist. Try Re-check in a moment.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const downloadCsv = () => {
    // Reuse the same filter params so the download matches what the admin sees.
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${API}/admin/beta-signups/export.csv?${params.toString()}`;
    // Fetch as blob (browser can't attach Authorization header via an <a> tag).
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`export failed (HTTP ${r.status})`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "myrichaunt-waitlist.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((e) => setErr(String(e.message || e)));
  };

  return (
    <section data-testid="waitlist-tab" className="space-y-6">
      {/* Rollup strip — role counts + top sources */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <RollupCard
          label="On the list"
          value={data ? data.total : "—"}
          icon={<Users className="h-4 w-4" strokeWidth={2} />}
          testid="waitlist-total"
        />
        <RollupCard
          label="Roles"
          testid="waitlist-roles"
          value={
            data ? (
              <span className="flex items-baseline gap-3 font-serif text-2xl leading-none text-ink">
                <span><span className="text-cobalt">{data.role_counts.donor || 0}</span> <span className="text-xs font-medium uppercase tracking-wide text-olive">donor</span></span>
                <span><span className="text-grass">{data.role_counts.student || 0}</span> <span className="text-xs font-medium uppercase tracking-wide text-olive">student</span></span>
                <span><span className="text-olive">{data.role_counts.either || 0}</span> <span className="text-xs font-medium uppercase tracking-wide text-olive">either</span></span>
              </span>
            ) : "—"
          }
        />
        <RollupCard
          label="Top sources"
          testid="waitlist-top-sources"
          value={
            data && data.top_sources.length ? (
              <ul className="mt-1 space-y-1">
                {data.top_sources.slice(0, 4).map((s) => (
                  <li key={s.source}>
                    <button
                      type="button"
                      onClick={() => { setSourceFilter(sourceFilter === s.source ? "" : s.source); setPage(1); }}
                      className={`inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[11px] transition-colors ${sourceFilter === s.source ? "bg-ink text-sand" : "bg-bone text-ink hover:bg-beige"}`}
                    >
                      <span className="font-mono">{s.source}</span>
                      <span className="text-[10px] font-semibold">{s.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : <span className="text-sm italic text-olive">No sources tagged yet.</span>
          }
        />
      </div>

      {/* Toolbar — search + role filter + CSV */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-bone px-4 py-3">
        <label className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-olive" strokeWidth={2} />
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by email"
            data-testid="waitlist-search"
            className="w-full rounded-full border border-border bg-sand py-2 pl-10 pr-3 text-sm text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
          />
        </label>

        <div className="flex items-center gap-1 rounded-full border border-border bg-sand p-1 text-xs font-semibold" data-testid="waitlist-role-filter">
          {[
            ["", "All"],
            ["donor", "Donors"],
            ["student", "Students"],
            ["either", "Either"],
          ].map(([v, label]) => (
            <button
              key={v || "all"}
              type="button"
              onClick={() => { setRoleFilter(v); setPage(1); }}
              className={`rounded-full px-3 py-1 transition-colors ${roleFilter === v ? "bg-ink text-sand" : "text-ink hover:bg-beige"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {sourceFilter && (
          <button
            type="button"
            onClick={() => setSourceFilter("")}
            data-testid="waitlist-clear-source"
            className="rounded-full bg-tangerine/15 px-3 py-1 text-xs font-semibold text-tangerine hover:bg-tangerine/25"
          >
            source: {sourceFilter} · clear
          </button>
        )}

        <button
          type="button"
          onClick={load}
          data-testid="waitlist-refresh"
          className="inline-flex items-center gap-1 rounded-full border border-border bg-sand px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2} /> Refresh
        </button>

        <button
          type="button"
          onClick={downloadCsv}
          disabled={loading || !data || data.total === 0}
          data-testid="waitlist-download-csv"
          className="ml-auto inline-flex items-center gap-1 rounded-full bg-ink px-4 py-1.5 text-xs font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" strokeWidth={2} /> Download CSV
        </button>
      </div>

      {err && (
        <p role="alert" className="rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet" data-testid="waitlist-error">
          {err}
        </p>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-sand">
        <table className="min-w-full text-sm">
          <thead className="bg-bone text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">
            <tr>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && !data && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-olive"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            )}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center">
                  <Mail className="mx-auto h-6 w-6 text-olive/50" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-olive">
                    {q || roleFilter || sourceFilter
                      ? "No signups match your filters."
                      : "No one's joined the waitlist yet."}
                  </p>
                </td>
              </tr>
            )}
            {data && data.rows.map((r, i) => (
              <motion.tr
                key={`${r.email}-${r.created_at}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(i * 0.01, 0.2) }}
                className="hover:bg-bone/60"
                data-testid={`waitlist-row-${i}`}
              >
                <td className="px-4 py-3 font-medium text-ink">{r.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${roleTone(r.role)}`}>
                    {r.role}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-olive">
                  {r.source || <span className="italic text-olive/60">—</span>}
                </td>
                <td className="px-4 py-3 text-olive">{fmtDate(r.created_at)}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-olive" data-testid="waitlist-pagination">
          <span>
            Showing <span className="font-semibold text-ink">{(page - 1) * PAGE_SIZE + 1}</span>–
            <span className="font-semibold text-ink">{Math.min(page * PAGE_SIZE, data.total)}</span> of{" "}
            <span className="font-semibold text-ink">{data.total}</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="rounded-full border border-border bg-sand px-3 py-1 font-semibold hover:border-ink disabled:opacity-50"
              data-testid="waitlist-page-prev"
            >
              Previous
            </button>
            <span className="font-semibold text-ink">Page {page} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="rounded-full border border-border bg-sand px-3 py-1 font-semibold hover:border-ink disabled:opacity-50"
              data-testid="waitlist-page-next"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const RollupCard = ({ label, value, icon, testid }) => (
  <div className="rounded-xl border border-border bg-bone p-5" data-testid={testid}>
    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
      {icon}{label}
    </p>
    <div className="mt-2 font-serif text-2xl leading-tight text-ink">{value}</div>
  </div>
);
